import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { currentLocale } from '../../i18n';
import { API_URL } from '../../utils/api';
import './AdminHost.css';

/**
 * Admin panel: NUC15-hostens läge.
 *
 * Visar vad som är installerat för highlight-rendering och streaming (CS2-builds
 * i ~/cs2-win och ~/cs2, HLAE, awpy-data-geometrin för siktkollen), hur den
 * nattliga uppdateringen (cs2-host-update) gick och vilka voice-lyssnare som kör.
 * Datat rapporteras varje minut av cs2-host-agent på hosten; agenten startar
 * och stoppar också ts3-/fluxer-listener efter guildernas röstplattform.
 * Owner-only i Django; anropet proxas via FastAPI (`/api/admin/host/status`).
 */
export default function AdminHost({ perms }) { // eslint-disable-line no-unused-vars
  const { t } = useTranslation();
  const { getAuthHeaders } = useAuth();

  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchStatus = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/admin/host/status`, { headers: getAuthHeaders() });
      if (r.ok) {
        setStatus(await r.json());
      }
    } catch {
      // silent — status är best-effort
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  /**
   * Formatera en unix-tid (sekunder) enligt användarens språk.
   *
   * @param {number|null} unix - Sekunder sedan epoch.
   * @returns {string} Datum och tid, eller "—".
   */
  const fmtUnix = (unix) => (unix ? new Date(unix * 1000).toLocaleString(currentLocale()) : '—');

  /**
   * Badge-klass för en voice-lyssnare: grön när läget matchar det önskade,
   * gul när agenten ännu inte hunnit ändra det, grå när den (korrekt) är stoppad.
   *
   * @param {string} state - Containerns läge (running/exited/missing …).
   * @param {boolean} wanted - Om någon guild använder plattformen.
   * @returns {string} CSS-klasser.
   */
  const listenerClass = (state, wanted) => {
    const running = state === 'running';
    if (running === wanted) return running ? 'host-badge host-badge-ok' : 'host-badge host-badge-idle';
    return 'host-badge host-badge-warn';
  };

  const report = status?.report;
  const lastRun = report?.host_update?.last_run;
  const listeners = report?.voice_listeners || {};

  /**
   * En rad i versionstabellen för en CS2-installation.
   *
   * @param {string} labelKey - i18n-nyckel för radens namn.
   * @param {object|null} inst - `{patch, version_date}` från agenten.
   * @returns {JSX.Element} Tabellraden.
   */
  const cs2Row = (labelKey, inst) => (
    <tr>
      <td>{t(labelKey)}</td>
      <td>{inst?.patch || t('admin.host.missing')}</td>
      <td>{inst?.version_date || '—'}</td>
    </tr>
  );

  return (
    <div>
      <h1 className="admin-section-title">{t('admin.host.title')}</h1>

      {loading && !status ? (
        <div className="admin-info">{t('live.loading')}</div>
      ) : !status ? (
        <div className="admin-info">{t('admin.host.noStatus')}</div>
      ) : (
        <>
          {status.stale ? (
            <div className="admin-error host-mb">{t('admin.host.stale')}</div>
          ) : (
            <div className="admin-card-desc host-mb">
              {t('admin.host.reportedAgo', { count: status.age_seconds })}
            </div>
          )}

          {/* ── Versioner ─────────────────────────────────────────── */}
          <div className="admin-card">
            <div className="admin-card-title">{t('admin.host.versionsTitle')}</div>
            <div className="admin-card-desc">{t('admin.host.versionsDesc')}</div>
            <div className="host-table-wrap">
              <table className="host-table">
                <thead>
                  <tr>
                    <th>{t('admin.host.colComponent')}</th>
                    <th>{t('admin.host.colVersion')}</th>
                    <th>{t('admin.host.colDetail')}</th>
                  </tr>
                </thead>
                <tbody>
                  {cs2Row('admin.host.cs2Render', report?.cs2?.render)}
                  {cs2Row('admin.host.cs2Stream', report?.cs2?.stream)}
                  <tr>
                    <td>{t('admin.host.hlae')}</td>
                    <td>{report?.hlae?.installed || t('admin.host.missing')}</td>
                    <td>{report?.hlae?.dir || '—'}</td>
                  </tr>
                  <tr>
                    <td>{t('admin.host.awpyData')}</td>
                    <td>{status.awpy_data_release || t('admin.host.missing')}</td>
                    <td>{t('admin.host.awpyDataDetail')}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* ── Nattlig uppdatering ──────────────────────────────── */}
          <div className="admin-card">
            <div className="admin-card-title">{t('admin.host.updateTitle')}</div>
            <div className="admin-card-desc">{t('admin.host.updateDesc')}</div>
            {!lastRun ? (
              <div className="admin-info">{t('admin.host.neverRun')}</div>
            ) : (
              <>
                <div className="admin-row">
                  <span className="admin-label">{t('admin.host.lastRun')}</span>
                  <span>
                    {fmtUnix(lastRun.finished_at)}{' '}
                    <span className={lastRun.ok ? 'host-badge host-badge-ok' : 'host-badge host-badge-danger'}>
                      {lastRun.ok ? t('admin.host.ok') : t('admin.host.failed')}
                    </span>
                  </span>
                </div>
                {lastRun.hlae_lag && (
                  <div className="admin-error host-mb">{lastRun.hlae_lag}</div>
                )}
                {lastRun.report?.length > 0 ? (
                  <ul className="host-report">
                    {lastRun.report.map((line) => <li key={line}>{line}</li>)}
                  </ul>
                ) : (
                  <div className="admin-card-desc">{t('admin.host.nothingToDo')}</div>
                )}
              </>
            )}
            <div className="admin-row">
              <span className="admin-label">{t('admin.host.nextRun')}</span>
              <span>{report?.host_update?.next_run || '—'}</span>
            </div>
          </div>

          {/* ── Voice-lyssnare ───────────────────────────────────── */}
          <div className="admin-card">
            <div className="admin-card-title">{t('admin.host.listenersTitle')}</div>
            <div className="admin-card-desc">{t('admin.host.listenersDesc')}</div>
            <div className="host-table-wrap">
              <table className="host-table">
                <thead>
                  <tr>
                    <th>{t('admin.host.colListener')}</th>
                    <th>{t('admin.host.colState')}</th>
                    <th>{t('admin.host.colWanted')}</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(status.desired_listeners).map(([svc, wanted]) => (
                    <tr key={svc}>
                      <td>{svc}</td>
                      <td>
                        <span className={listenerClass(listeners[svc], wanted)}>
                          {listeners[svc] || '?'}
                        </span>
                      </td>
                      <td>{wanted ? t('admin.host.yes') : t('admin.host.no')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <button className="admin-btn admin-btn-secondary" onClick={fetchStatus} disabled={loading}>
            {t('admin.host.refresh')}
          </button>
        </>
      )}
    </div>
  );
}
